/*
 * Reviews flyout drawer
 *
 * Opens the Okendo reviews widget in a right-side flyout that mirrors the cart
 * drawer's motion and size. The panel markup lives in
 * snippets/reviews-drawer.liquid as <reviews-drawer class="drawer">, rendered
 * globally on every PDP from sections/product.liquid.
 *
 * Motion is inherited from theme.css:
 *   .is-open .drawer__inner   -> slideInRight
 *   .is-closing .drawer__inner -> slideOutRight
 *   .is-open .underlay        -> backdrop fades in
 *
 * Trigger: clicking the top star-rating app block, which Okendo renders with
 * [data-oke-star-rating] (and a review-count link). We use a delegated document
 * listener so it survives Okendo's internal markup.
 */
(function () {
  if (window.__reviewsDrawerInit) return;
  window.__reviewsDrawerInit = true;

  var OPEN = 'is-open';
  var CLOSING = 'is-closing';
  var SCROLL_LOCK = 'reviews-drawer-open';

  function getDrawer() {
    return document.querySelector('[data-reviews-drawer]');
  }

  function isOpen(drawer) {
    return drawer.classList.contains(OPEN);
  }

  // Theme a11y helpers (set on window.theme.a11y by theme.js). Guarded so the
  // drawer still works if the theme bundle hasn't defined them.
  function a11y() {
    return window.theme && window.theme.a11y;
  }

  function open(drawer) {
    if (isOpen(drawer)) return;
    drawer.classList.remove(CLOSING);
    drawer.classList.add(OPEN);
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(SCROLL_LOCK);

    // Lock page scroll the same way the cart flyout does (touch-aware; keeps
    // the drawer's [data-scroll-lock-scrollable] inner scrollable).
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

    // Trap keyboard focus inside the flyout once it has slid in.
    var inner = drawer.querySelector('.drawer__inner');
    var trap = function () {
      var helpers = a11y();
      var closeButton = drawer.querySelector('[data-reviews-drawer-close]');
      if (helpers && typeof helpers.trapFocus === 'function') {
        helpers.trapFocus(drawer, { elementToFocus: closeButton });
      } else if (inner) {
        inner.setAttribute('tabindex', '-1');
        inner.focus({ preventScroll: true });
      }
      if (inner) inner.removeEventListener('animationend', trap);
    };
    if (inner) {
      inner.addEventListener('animationend', trap);
      // Fallback in case animationend doesn't fire.
      setTimeout(trap, 400);
    } else {
      trap();
    }
  }

  function close(drawer) {
    if (!isOpen(drawer)) return;
    var inner = drawer.querySelector('.drawer__inner');
    drawer.classList.add(CLOSING);
    drawer.classList.remove(OPEN);
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(SCROLL_LOCK);

    var helpers = a11y();
    if (helpers && typeof helpers.removeTrapFocus === 'function') {
      helpers.removeTrapFocus();
      if (typeof helpers.autoFocusLastElement === 'function') {
        helpers.autoFocusLastElement();
      }
    }

    // Release the page scroll lock (mirrors the cart flyout).
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

    var done = function () {
      drawer.classList.remove(CLOSING);
      if (inner) inner.removeEventListener('animationend', done);
    };
    if (inner) {
      inner.addEventListener('animationend', done);
      // Fallback in case animationend doesn't fire.
      setTimeout(done, 400);
    } else {
      done();
    }
  }

  // Elements that should open the drawer when clicked (the top stars + count).
  // Widened to the whole star block so the full area is clickable, and tolerant
  // of Okendo's various markups.
  function triggerEl(target) {
    if (!target || !target.closest) return null;
    try {
      var sel = [
        '[data-oke-star-rating]',
        '[data-oke-reviews-count]',
        '[data-oke-reviews-link]',
        '.product__block:has([data-oke-star-rating])',
        'a[href*="okendo-reviews"]',
        'a[href="#reviews-drawer"]'
      ].join(',');
      return target.closest(sel);
    } catch (e) {
      return (
        target.closest('[data-oke-star-rating]') ||
        target.closest('[data-oke-reviews-count]')
      );
    }
  }

  // Capture phase so we run BEFORE Okendo's own handlers (which may call
  // stopPropagation and would otherwise swallow the click).
  document.addEventListener(
    'click',
    function (event) {
      var drawer = getDrawer();
      if (!drawer) return;

      // Close controls.
      if (event.target.closest &&
          (event.target.closest('[data-reviews-drawer-close]') ||
           event.target.closest('[data-reviews-drawer-underlay]'))) {
        event.preventDefault();
        close(drawer);
        return;
      }

      // Open from the star rating / review count.
      if (triggerEl(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        open(drawer);
      }
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var drawer = getDrawer();
    if (drawer && isOpen(drawer)) {
      close(drawer);
    }
  });
})();
